/**
 * A buffer for text input used for line remarks.
 */
export class LineRemarkBufferBag {
    private readonly remarks: Map<number, string> = new Map();
    private readonly errors: Map<number, Array<string>> = new Map();

    /**
     * Get the line remark buffer for a given line id.
     *
     * @param lineId The line id to find where the remark buffer is stored
     */
    public get(lineId: number): string {
        return this.remarks.get(lineId) ?? '';
    }

    /**
     * Set the line remark buffer for a given line id.
     *
     * @param lineId The line id to find where the remark buffer is stored
     * @param value The value to set for the line remark buffer
     */
    public set(lineId: number, value: string): void {
        this.remarks.set(lineId, value);
        // Clear errors when setting a new value
        this.errors.delete(lineId);
    }

    /**
     * Check if there is a line remark buffer for a given line id
     *
     * @param lineId The line id to find where the remark buffer is stored
     */
    public has(lineId: number): boolean {
        return this.remarks.has(lineId);
    }

    /**
     * Delete the line remark buffer for a given line id
     *
     * @param lineId The line id to find where the remark buffer is stored
     */
    public delete(lineId: number): void {
        this.remarks.delete(lineId);
        this.errors.delete(lineId);
    }

    /**
     * Add an error message for a given line id
     *
     * @param lineId The line id to find where the error message is stored
     * @param error The error message to add for the line remark buffer
     */
    public addError(lineId: number, error: string): void {
        this.getOrInitErrors(lineId).push(error);
    }

    /**
     * Get or initialize the error messages array for a given line id
     *
     * @param lineId The line id to find where the error messages are stored
     */
    private getOrInitErrors(lineId: number): Array<string> {
        let arr = this.errors.get(lineId);
        if (!arr) {
            arr = [];
            this.errors.set(lineId, arr);
        }
        return arr;
    }

    /**
     * Add multiple error messages for a given line id
     *
     * @param lineId The line id to find where the error messages are stored
     * @param errors The error messages to add for the line remark buffer
     */
    public addManyError(lineId: number, errors: Array<string>): void {
        this.getOrInitErrors(lineId).push(...errors);
    }

    /**
     * Check if there is any error message for a given line id
     *
     * @param lineId The line id to find where the error messages are stored
     */
    public hasAnyError(lineId: number): boolean {
        return (this.errors.get(lineId)?.length ?? 0) > 0;
    }

    /**
     * Get the error messages for a given line id
     *
     * @param lineId The line id to find where the error messages are stored
     */
    public getErrors(lineId: number): Array<string> {
        return [...(this.errors.get(lineId) ?? [])];
    }

    /**
     * Get the error messages as a single string for a given line id
     *
     * @param lineId The line id to find where the error messages are stored
     */
    public getErrorsAsString(lineId: number): string {
        return this.getErrors(lineId).join(', ');
    }

    /**
     * Delete the error messages for a given line id
     *
     * @param lineId The line id to find where the error messages are stored
     */
    public deleteErrors(lineId: number): void {
        this.errors.delete(lineId);
    }

    /**
     * Clear the line remark buffer and its errors for a given line id
     */
    public clear(): void {
        this.remarks.clear();
        this.errors.clear();
    }
}